import React from 'react'
import { FormikHelpers } from 'formik'
import { Box } from 'components/Layout'
import {
  getErrorMessage,
  useLocationForm,
  LocationFormValues,
  LocationFieldName,
  CoordinateInput,
  useAddTestLocation,
  useEditTestLocation,
  TestLocations_testLocations,
} from '@modmd/data'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import { StatesDropdown } from 'sharedComponents/StatesDropdown'
import { Checkbox } from 'components/Checkbox'
import { KMLUpload } from 'components/Upload'
import { Coordinates } from './Coordinates'

interface Props {
  data: TestLocations_testLocations | undefined
  onDismiss: () => void
}

const CreateLocation = ({ data, onDismiss }: Props) => {
  const [addTestLocation] = useAddTestLocation()
  const [editTestLocation] = useEditTestLocation()

  const locationForm = useLocationForm({
    validationSchema: 'default',
    onSubmit: async (
      values: LocationFormValues,
      formikHelpers: FormikHelpers<LocationFormValues>
    ) => {
      if (!data) {
        await addTestLocation({
          variables: {
            inputData: {
              ...values,
            },
          },
        })
      } else {
        await editTestLocation({
          variables: {
            inputData: {
              id: values.id || '',
              name: values.name,
              state: values.state,
              coordinatesBounds: values.coordinatesBounds.map(({ lat, lon }: CoordinateInput) => ({
                lat,
                lon,
              })),
              isDefaultPrice: values.isDefaultPrice,
            },
          },
        })
      }
      formikHelpers.resetForm()
      onDismiss()
    },
  })

  React.useEffect(
    () => {
      const loadAndValidate = async () => {
        if (data)
          void (await locationForm.setValues(
            {
              ...data,
            },
            true
          ))
        await locationForm?.validateForm()
      }
      void loadAndValidate()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleAddressChange = React.useCallback(
    (coordinate: CoordinateInput[]) => {
      void locationForm.setFieldValue('coordinatesBounds', coordinate, true)
    },
    [locationForm]
  )

  return (
    <>
      <Box display="grid" marginTop="1rem">
        <TextInput
          name={LocationFieldName.NAME}
          type="text"
          value={locationForm?.values?.name}
          onChange={locationForm.handleChange}
          onBlur={async () => {
            await locationForm.setFieldTouched(LocationFieldName.NAME)
            await locationForm.validateField(LocationFieldName.NAME)
          }}
          errorMessage={getErrorMessage(locationForm?.touched.name, locationForm?.errors.name)}
          label="Name"
          isFullWidth
        />
        <StatesDropdown
          value={locationForm?.values?.state}
          onChange={(e) => locationForm.setFieldValue('state', e)}
          hasBottomContent
          errorMessage={getErrorMessage(locationForm?.touched.state, locationForm?.errors.state)}
          onBlur={async () => {
            await locationForm.setFieldTouched(LocationFieldName.STATE)
            await locationForm.validateField(LocationFieldName.STATE)
          }}
        />
      </Box>
      <Checkbox
        name={LocationFieldName.IS_DEFAULT_PRICE}
        checked={locationForm?.values?.isDefaultPrice}
        onChange={locationForm.handleChange}
        label="Default price"
      />
      <KMLUpload onChange={handleAddressChange} />
      <Coordinates
        onChange={handleAddressChange}
        data={data?.coordinatesBounds}
        onBlur={async () => {
          await locationForm.setFieldTouched(LocationFieldName.COORDINATES)
        }}
        errorMessage={
          locationForm?.errors.coordinatesBounds as Array<{ lat: string; lon: string }> | undefined
        }
        kmlValues={locationForm?.values.coordinatesBounds}
        touched={locationForm?.touched?.coordinatesBounds}
      />
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Button
          disabled={!!Object.keys(locationForm?.errors).length}
          onClick={() => locationForm.handleSubmit()}
        >
          Save
        </Button>
      </Box>
    </>
  )
}

export { CreateLocation }
